import { zodTransformFirestoreTimestampToDate } from "../../../utils/zodUtils.mjs";
import { zodFlavorId } from "../../ids.mjs";
import { defineFirestoreCollection } from "../../firebaseCollectionSchema.mjs";
import { PiiCategory, meta } from "../../meta.mjs";
import { executionTraceStampSchema } from "../../executionTrace.mjs";
import { bridgeMappedNoteFieldKeySchema } from "./mappedNote.mjs";
import { VendorSyncOps } from "../../vendorSync/vendorSync.mjs";
import { z as z$1 } from "zod";

// src/models/vendors/bridge/service.ts
const firestoreDate = z$1.any().transform(zodTransformFirestoreTimestampToDate);
const bridgeServiceStatusWriteSchema = z$1.enum([
	"PENDING",
	"BILLING",
	"SETTLING",
	"COMPLETE",
	"TRIAGE",
	"CANCELED",
	"DENIED"
]);
/** Bridge Service status values observed from Bridge API. */
const bridgeServiceStatusSchema = z$1.preprocess((value) => {
	if (typeof value === "string") return normalizeBridgeServiceStatusInput(value);
	return value;
}, bridgeServiceStatusWriteSchema);
function normalizeBridgeServiceStatusInput(status) {
	const upper = status.toUpperCase();
	return upper === "CANCELLED" ? "CANCELED" : upper;
}
function parseBridgeServiceStatus(status) {
	return bridgeServiceStatusSchema.parse(normalizeBridgeServiceStatusInput(status));
}
/** Vendor cancel unwind only — claims `DENIED` is not a consult cancel. */
function isBridgeServiceCanceledStatus(status) {
	if (!status) return false;
	const upper = status.toUpperCase();
	return upper === "CANCELED" || upper === "CANCELLED";
}
const bridgeServiceConsultCompleteTerminals = /* @__PURE__ */ new Set([
	"COMPLETE",
	"CANCELED",
	"DENIED"
]);
/**
* Bridge Service terminals that close the consult as lifecycle `completed`
* (visit happened). `DENIED` and `CANCELED` are claims outcomes, not cancel.
*/
function isBridgeServiceConsultCompleteTerminal(status) {
	if (!status) return false;
	try {
		return bridgeServiceConsultCompleteTerminals.has(parseBridgeServiceStatus(status));
	} catch {
		return false;
	}
}
/** Claims closed without a paid COMPLETE — skip settlement audit, still complete. */
function isBridgeServiceDeniedOrCanceledStatus(status) {
	if (!status) return false;
	try {
		const parsed = parseBridgeServiceStatus(status);
		return parsed === "DENIED" || parsed === "CANCELED";
	} catch {
		return false;
	}
}
const bridgeServicePostBillStatuses = /* @__PURE__ */ new Set([
	"BILLING",
	"SETTLING",
	"TRIAGE",
	"COMPLETE",
	"CANCELED",
	"DENIED"
]);
/** Bridge Service statuses after Note+Bill submit (or terminal post-create). */
function isBridgeServicePostBillStatus(status) {
	if (!status) return false;
	try {
		return bridgeServicePostBillStatuses.has(parseBridgeServiceStatus(status));
	} catch {
		return false;
	}
}
/**
* Bridge service observed doc — doc id = consultationId (Bridge `externalId`).
* Path: `bridge_services/{consultationId}`.
*/
const bridgeServiceSchema = z$1.object({
	id: zodFlavorId(),
	customerId: zodFlavorId(),
	consultationId: zodFlavorId(),
	vendorNativeId: zodFlavorId().optional(),
	serviceEligibilityId: zodFlavorId().optional(),
	status: bridgeServiceStatusSchema,
	dateOfService: z$1.string().optional(),
	/** Bridge Note id from last successful Create (L4, non-PHI). */
	bridgeNoteId: z$1.string().optional(),
	/** Bridge addendum id from last successful addendum POST (L4, non-PHI). */
	bridgeNoteAddendumId: z$1.string().optional(),
	/** SHA-256 of canonical mapped Bridge Note `data` last successfully sent. */
	lastSentMappedPayloadHash: z$1.string().optional(),
	/** Canvas note checksum at last evaluate/send (L4, non-PHI). */
	canvasNoteChecksum: z$1.string().optional(),
	/** Canvas note REST snapshot hash at last evaluate/send (L4, non-PHI). */
	canvasNoteObservedSnapshotHash: z$1.string().optional(),
	/** Canvas note_changed ingress event id at last evaluate/send (L4, non-PHI). */
	canvasNoteObservedAmendEventId: z$1.string().optional(),
	/** Canvas note state at last evaluate/send (L4, non-PHI). */
	canvasNoteRevision: z$1.string().optional(),
	/** True when v1 mapped keys are complete after evaluate. */
	mappedNoteReady: z$1.boolean().optional(),
	/** Missing v1 mapped field names only — no chart values. */
	mappedNoteMissingFields: z$1.array(bridgeMappedNoteFieldKeySchema).optional(),
	/**
	* Last Bridge note POST rejection (field + validation type). No chart values.
	* Cleared when a later evaluate hashes a different payload, or when create/addendum succeeds.
	*/
	mappedNotePostError: z$1.string().nullish(),
	/** Hash of the mapped payload Bridge rejected. No chart values. */
	mappedNotePostErrorPayloadHash: z$1.string().nullish(),
	snapshot: meta(z$1.record(z$1.string(), z$1.unknown()).optional(), {
		bqType: "stringified_map",
		pii: { category: "health" }
	}),
	observedAt: firestoreDate.optional(),
	createdAt: firestoreDate,
	updatedAt: firestoreDate,
	executionTrace: executionTraceStampSchema.optional()
});
const BridgeServices = defineFirestoreCollection("bridge_services", bridgeServiceSchema, { ops: VendorSyncOps }, {
	dwReplication: true,
	firestoreCollectionPath: "bridge_services",
	executionTrace: true
});

export { BridgeServices, bridgeServiceSchema, bridgeServiceStatusSchema, isBridgeServiceCanceledStatus, isBridgeServiceConsultCompleteTerminal, isBridgeServiceDeniedOrCanceledStatus, isBridgeServicePostBillStatus, normalizeBridgeServiceStatusInput, parseBridgeServiceStatus };
//# sourceMappingURL=service.mjs.map